import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { PopoverModule } from 'primeng/popover';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SliderModule } from 'primeng/slider';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { firstValueFrom } from 'rxjs';
import { MessageService } from 'src/app/services/message.service';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import { OpenCloseStoresGQL, OpenCloseStoresQuery, OpenCloseStoreFilter, LoggingGQL } from 'src/gql/generated';

type OpenCloseStore = OpenCloseStoresQuery['openCloseStores'][number];
type StoreArea = OpenCloseStoresQuery['storeAreas'][number];

@Component({
  selector: 'app-open-close',
  templateUrl: './open-close.component.html',
  styleUrls: ['./open-close.component.sass'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    CheckboxModule,
    ButtonModule,
    PopoverModule,
    PanelModule,
    ProgressSpinnerModule,
    SliderModule,
    SelectButtonModule,
    SelectModule,
    MultiSelectModule,
    MessageModule,
    BadgeModule,
    TagModule,
    ToggleSwitchModule,
  ],
})
export class OpenCloseComponent implements OnInit {
  isLoading = false;
  filterForm: FormGroup;
  stores: OpenCloseStore[] = [];
  allStoreAreas: StoreArea[] = [];
  availableStoreAreas: StoreArea[] = [];
  chainOptions: string[] = [];

  targetDateRangeOptions = [
    { label: '今後7日間', value: 7 },
    { label: '今後14日間', value: 14 },
    { label: '今後30日間', value: 30 },
    { label: '今後90日間', value: 90 },
    { label: '今日', value: 0 },
    { label: '全て', value: null },
  ];

  lastUpdateDateRangeOptions = [
    { label: '7日以内', value: 7 },
    { label: '14日以内', value: 14 },
    { label: '30日以内', value: 30 },
    { label: '90日以内', value: 90 },
    { label: '今日', value: 0 },
    { label: '全て', value: null },
  ];

  storeTypeOptions = [
    { label: '全て', value: null },
    { label: 'OPEN', value: 'OPEN' },
    { label: 'CLOSE', value: 'CLOSE' },
  ];

  // Keys for localStorage
  private readonly filterStorageKey = 'openCloseFilter';

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private subtitleService: SubtitleService,
    private routingService: RoutingService,
    private userService: UserService,
    private openCloseStoresGQL: OpenCloseStoresGQL,
    private loggingGql: LoggingGQL,
  ) {
    this.routingService.loginRequired();
    // this.userService.spreadsheetRequired(); // If needed
    this.subtitleService.setSubTitle('開店閉店情報');

    this.filterForm = this.fb.group({
      targetDateRange: [30], // Default to '今後30日間'
      lastUpdateDateRange: [30], // Default to '30日以内'
      storeAreaIds: [[]],
      storeType: [null], // New filter for store type (OPEN/CLOSE)
      chainNames: [[]], // Multiple chain names can be selected
    });
  }

  ngOnInit(): void {
    this.loadFiltersFromStorage();
    this.fetchData();
    this.fetchStoreAreasForFilter(); // Assuming storeAreas are fetched alongside or separately
  }

  private loadFiltersFromStorage(): void {
    const savedFilters = localStorage.getItem(this.filterStorageKey);
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      // Ensure storeAreaIds is an array when loading from storage
      if (parsedFilters.storeAreaIds === null || typeof parsedFilters.storeAreaIds === 'undefined') {
        parsedFilters.storeAreaIds = [];
      } else if (!Array.isArray(parsedFilters.storeAreaIds)) {
        // If it was stored as a single value (e.g. from a previous version), wrap it in an array
        parsedFilters.storeAreaIds = [parsedFilters.storeAreaIds].filter((id) => id !== null);
      }
      this.filterForm.patchValue(parsedFilters);
    }
  }

  private saveFiltersToStorage(): void {
    localStorage.setItem(this.filterStorageKey, JSON.stringify(this.filterForm.value));
  }

  async fetchData(): Promise<void> {
    this.isLoading = true;
    this.saveFiltersToStorage(); // Save current filters before fetching

    const filterValue = this.filterForm.value;
    const gqlFilter: OpenCloseStoreFilter = {};

    // Handle Target Date Range
    if (filterValue.targetDateRange !== null && typeof filterValue.targetDateRange !== 'undefined') {
      if (filterValue.targetDateRange === 0) {
        // Today
        gqlFilter.fromTargetDate = moment().startOf('day').format('YYYY-MM-DD');
        gqlFilter.toTargetDate = moment().endOf('day').format('YYYY-MM-DD');
      } else {
        // Future N days
        gqlFilter.fromTargetDate = moment().startOf('day').format('YYYY-MM-DD');
        gqlFilter.toTargetDate = moment().add(filterValue.targetDateRange, 'days').endOf('day').format('YYYY-MM-DD');
      }
    }

    // Handle Last Update Date Range
    if (filterValue.lastUpdateDateRange !== null && typeof filterValue.lastUpdateDateRange !== 'undefined') {
      if (filterValue.lastUpdateDateRange === 0) {
        // Today
        gqlFilter.fromLastUpdate = moment().startOf('day').format('YYYY-MM-DD');
      } else {
        // Past N days
        gqlFilter.fromLastUpdate = moment()
          .subtract(filterValue.lastUpdateDateRange, 'days')
          .startOf('day')
          .format('YYYY-MM-DD');
      }
    }

    // Handle storeAreaIds for GQL filter
    // If only one area is selected, pass it to GQL. Otherwise, fetch all and filter client-side.
    if (filterValue.storeAreaIds && filterValue.storeAreaIds.length === 1) {
      gqlFilter.storeAreaId = filterValue.storeAreaIds[0];
    }
    // If multiple areas are selected, gqlFilter.storeAreaId remains undefined (fetch all)

    // this.isLoading = true; // Already set at the beginning of fetchData
    try {
      const result = await firstValueFrom(this.openCloseStoresGQL.fetch({ variables: { input: gqlFilter } }));
      let fetchedStores = (result.data.openCloseStores as OpenCloseStore[]) || [];

      // Client-side filtering for storeAreaIds if multiple areas were selected
      if (filterValue.storeAreaIds && filterValue.storeAreaIds.length > 1) {
        fetchedStores = fetchedStores.filter(
          (store) => store.storeArea && filterValue.storeAreaIds.includes(store.storeArea.id),
        );
      }

      // Client-side filtering for storeType
      if (filterValue.storeType) {
        fetchedStores = fetchedStores.filter((store) => store.type === filterValue.storeType);
      }

      // Client-side filtering for chainNames
      if (filterValue.chainNames && filterValue.chainNames.length > 0) {
        fetchedStores = fetchedStores.filter((store) => {
          const chainPrefix = store.chainName?.split('-')[0]?.trim();
          return chainPrefix && filterValue.chainNames.includes(chainPrefix);
        });
      }

      // Update chain options after filtering
      this.updateChainOptions(fetchedStores);
      this.stores = fetchedStores;

      if (result.data.storeAreas) {
        this.allStoreAreas = (result.data.storeAreas as StoreArea[]) || [];
      }

      this.updateAvailableStoreAreas(this.stores);
    } catch (error) {
      this.messageService.setFlashMessageWithClear('error', 'エラー', 'データの取得に失敗しました。');
      console.error('Error fetching open-close stores:', error);
      this.stores = [];
    } finally {
      this.isLoading = false;
      // Log the GQL filter and client-side filters
      const toAreaName = (id: string) => {
        const a = this.allStoreAreas.find((x) => x.id === id);
        return a ? a.name : id;
      };
      const gqlQueryForLog: any = { ...gqlFilter };
      if (gqlQueryForLog.storeAreaId) {
        gqlQueryForLog.storeAreaName = toAreaName(gqlQueryForLog.storeAreaId);
        delete gqlQueryForLog.storeAreaId;
      }
      const fullFilterForLogging = {
        gqlQuery: gqlQueryForLog,
        clientSideStoreAreaNames:
          filterValue.storeAreaIds && filterValue.storeAreaIds.length > 1
            ? filterValue.storeAreaIds.map(toAreaName)
            : undefined,
        clientSideStoreType: filterValue.storeType,
      };
      this.logging(fullFilterForLogging);
    }
  }

  async fetchStoreAreasForFilter(): Promise<void> {
    // This method is a placeholder if storeAreas need to be fetched separately.
    // If OpenCloseStoresGQL is modified to return storeAreas, this might not be needed
    // or could be merged into fetchData.
    // For now, assuming it's part of the main query or handled by it.
    // If a separate GQL query is needed:
    // try {
    //   const result = await firstValueFrom(this.someStoreAreasGQL.fetch());
    //   this.allStoreAreas = result.data.storeAreas || [];
    // } catch (error) {
    //   this.messageService.showError('店舗エリアの取得に失敗しました。', error);
    // }
  }

  onFilterChange(): void {
    this.fetchData();
  }

  resetFilters(): void {
    this.filterForm.reset();
    this.saveFiltersToStorage();
    this.fetchData();
  }

  private updateAvailableStoreAreas(stores: OpenCloseStore[]): void {
    const availableAreaIds = new Set(stores.map((store) => store.storeArea?.id).filter((id): id is string => !!id));
    this.availableStoreAreas = this.allStoreAreas
      .filter((area) => availableAreaIds.has(area.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private updateChainOptions(stores: OpenCloseStore[]): void {
    // Get unique chain prefixes
    const chainPrefixes = new Set(
      stores.map((store) => store.chainName?.split('-')[0]?.trim()).filter((prefix) => prefix), // Remove undefined/null/empty values
    );
    this.chainOptions = Array.from(chainPrefixes).sort();
  }

  private async logging(filter: any) {
    // Changed type to any to accommodate richer log object
    try {
      await firstValueFrom(
        this.loggingGql.fetch({
          variables: {
            name: 'open-close',
            log: JSON.stringify({
              user: this.userService.getName(),
              filter: filter, // Log the combined GQL and client-side filters
            }),
          },
        }),
      );
    } catch (e) {
      console.error('Error logging open-close access:', e);
    }
  }

  // Helper for template to get store area name
  getStoreAreaName(storeAreaId: string): string {
    const area = this.allStoreAreas.find((a) => a.id === storeAreaId);
    return area ? area.name : 'N/A';
  }
}
